import React, { useState } from 'react';
import { Col, Card, Button, Dropdown, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const ProductCard = ({ title, description, image_url, variants = [], product_id }) => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(variants[0] || {});

  const handleIncrement = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const handleDecrement = () => {
    setQuantity((prevQuantity) => (prevQuantity > 1 ? prevQuantity - 1 : 1));
  };

  const handleAddToCart = async () => {
    const user_id = localStorage.getItem('user_id');
    const token = localStorage.getItem('authToken');

    if (!selectedVariant || !selectedVariant.product_variant_id) {
      console.error('No valid variant selected.');
      return;
    }

    const cartData = {
      product_id,
      product_variants_id: selectedVariant.product_variant_id,
      quantity,
      user_id,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_HOST}api/cart`, cartData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Show success SweetAlert
      Swal.fire({
        icon: 'success',
        title: 'Product Added!',
        text: `${title} has been added to your cart.`,
        showConfirmButton: true,
        confirmButtonText: 'OK',
        timer: 2000,
      });

      console.log('Item added to cart:', response.data);
    } catch (error) {
      console.error('Error adding to cart:', error);

      // Show error SweetAlert
      Swal.fire({
        icon: 'error',
        title: 'Oops!',
        text: 'Something went wrong. Please try again.',
        showConfirmButton: true,
        confirmButtonText: 'OK',
      });
    }
  };

  const fullImageUrl = `${process.env.REACT_APP_HOST}storage/${image_url}`;

  return (
    <Col xs={6} sm={6} md={4} lg={3} className="p-1">
      <Card className="shadow-lg h-100">
        <Card.Img
          variant="top"
          src={fullImageUrl}
          style={{
            width: '100%',
            aspectRatio: '1',
            objectFit: 'contain',
            padding: '1rem',
          }}
        />
        <Card.Body style={{ padding: '0 1rem 0.5rem' }}>
          <Card.Title style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>{title}</Card.Title>
          <Card.Text style={{ color: '#555', fontSize: '0.95rem', margin: '3px' }}>{description}</Card.Text>

          {/* Dropdown for Variants */}
          <Form.Group className="mb-3">
            <Dropdown>
              <Dropdown.Toggle
                id="dropdown-basic-button"
                className="w-100"
                style={{
                  fontSize: '11px',
                  padding: '6px',
                  border: '1px solid rgb(221, 221, 221)',
                  borderRadius: '8px',
                  color: 'rgb(51, 51, 51)',
                  backgroundColor: 'rgb(245, 245, 245)',
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 5px',
                  transition: '0.3s ease-in-out',
                  display: 'flex',
                  justifyContent: 'space-between',
                  overflow: 'auto',
                  wordWrap: 'break-word',
                  WebkitLineClamp: '5',
                }}
              >
                {selectedVariant.unit_quantity
                  ? `${selectedVariant.unit_quantity} ${selectedVariant.unit_title || ''} - ₹${selectedVariant.price} ${selectedVariant.discount > 0
                    ? `(${selectedVariant.discount}% Off | ₹${selectedVariant.price - (selectedVariant.price * selectedVariant.discount) / 100})`
                    : ''
                  }`
                  : 'Select a Weight'}
                <i className="bi bi-chevron-down" style={{ fontSize: '1.2rem', marginLeft: '10px' }}></i>
              </Dropdown.Toggle>

              <Dropdown.Menu
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                  overflow: 'hidden',
                }}
              >
                {variants.length > 0 ? (
                  variants.map((variant) => (
                    <Dropdown.Item
                      key={variant.product_variant_id}
                      onClick={() => setSelectedVariant(variant)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 15px',
                        fontWeight: variant.discount > 0 ? 'bold' : 'normal',
                        color: '#333',
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s ease-in-out',
                      }}
                      onMouseEnter={(e) => (e.target.style.backgroundColor = '#f9f9f9')}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = '#fff')}
                    >
                      <span>{`${variant.unit_quantity} ${variant.unit_title || ''}`}</span>
                      <span>
                        ₹{variant.price}{' '}
                        {variant.discount > 0 && (
                          <span style={{ color: 'green', fontSize: '0.9rem' }}>
                            ({variant.discount}% Off | ₹
                            {variant.price - (variant.price * variant.discount) / 100})
                          </span>
                        )}
                      </span>
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.Item
                    disabled
                    style={{
                      textAlign: 'center',
                      color: '#aaa',
                      padding: '12px 15px',
                    }}
                  >
                    No Variants Available
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {/* Quantity Controls */}
          {quantity > 0 ? (
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Button variant="outline-danger" onClick={handleDecrement} size="lg" style={{ padding: '0', width: '40px', height: '40px' }}>
                -
              </Button>
              <span className="badge bg-danger">{quantity}</span>
              <Button variant="outline-danger" onClick={handleIncrement} size="lg" style={{ padding: '0', width: '40px', height: '40px' }}>
                +
              </Button>
            </div>
          ) : (
            <Button variant="danger" onClick={() => setQuantity(1)} className="w-100 mb-3">
              Add
            </Button>
          )}

          <div className="col">
            <Button variant="success" onClick={handleAddToCart} className="add-to-cart w-100">
              Add to Cart
            </Button>
            <style jsx>{`
      @media (max-width: 768px) {
  .add-to-cart {
    margin-bottom: 20px; /* Adjust as needed */
  
  }

  .footer {
    position: relative;
    z-index: 0;
  }
}
  `}</style>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default ProductCard;
